import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceCategoryService } from '@/api/generated/services/PerformanceCategoryService'
import { PerformanceCategoryUpdateRequest } from '@/api/generated/models/PerformanceCategoryUpdateRequest'
import { PerformanceTreeNode } from '../types'
import { getPerformanceTypeLabel } from '../utils/performanceUtils'
import { useAlert } from '@/components/ui/Alert/exports'
import RequiredLabel from '@/components/ui/RequiredLabel'

interface PerformanceCategoryEditPanelProps {
  isOpen: boolean
  onClose: () => void
  performanceCategory: PerformanceTreeNode | null
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string | React.ReactNode
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceCategoryEditPanel: React.FC<PerformanceCategoryEditPanelProps> = ({
  isOpen,
  onClose,
  performanceCategory,
  onSuccess
}) => {
  const { showError } = useAlert()
  
  // Form state
  const [categoryName, setCategoryName] = useState('')
  const [manualWeight, setManualWeight] = useState<number | null>(null)
  const [isManualWeight, setIsManualWeight] = useState(false)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form with current data when panel opens
  useEffect(() => {
    if (isOpen && performanceCategory) {
      setCategoryName(performanceCategory.name || '')
      // Initialize manual weight if it exists
      const currentWeight = (performanceCategory as any).manualWeight
      if (currentWeight !== null && currentWeight !== undefined) {
        setManualWeight(currentWeight)
        setIsManualWeight(true)
      } else {
        setManualWeight(null)
        setIsManualWeight(false)
      }
    }
  }, [isOpen, performanceCategory])

  const handleSubmit = async () => {
    if (!performanceCategory?.id) {
      showError('하위분류 정보가 없습니다.')
      return
    }

    if (!categoryName.trim()) {
      showError('하위분류명을 입력해주세요.')
      return
    }

    setIsSubmitting(true)

    try {
      const updateRequest: PerformanceCategoryUpdateRequest = {
        name: categoryName.trim(),
        // 수동 가중치가 설정된 경우에만 포함
        ...(isManualWeight && manualWeight !== null ? { manual_weight: manualWeight } : {}),
      }

      await PerformanceCategoryService.appsPerformanceViewsUpdatePerformanceCategory({
        categoryId: performanceCategory.id,
        requestBody: updateRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to update performance category:', err)
      
      // Extract error details from response
      const errorData = err?.body || err?.response?.data || err
      const errorMessages = []
      
      // Collect all available error information
      if (errorData?.details) {
        if (Array.isArray(errorData.details)) {
          errorMessages.push(...errorData.details)
        } else if (typeof errorData.details === 'object') {
          // JSON 객체인 경우 문자열로 변환
          errorMessages.push(JSON.stringify(errorData.details))
        } else {
          errorMessages.push(errorData.details)
        }
      }
      
      if (errorData?.message) {
        errorMessages.push(errorData.message)
      }
      
      if (errorData?.error) {
        errorMessages.push(errorData.error)
      }
      
      // If we have specific error messages, use them
      if (errorMessages.length > 0) {
        showError(errorMessages.join(' / '))
      } else {
        // Fallback to status-based messages
        if (err && typeof err === 'object' && 'status' in err) {
          switch (err.status) {
            case 400:
              showError('입력 데이터가 올바르지 않습니다.')
              break
            case 401:
              showError('인증이 만료되었습니다. 다시 로그인해주세요.')
              break
            case 404:
              showError('수정하려는 하위분류를 찾을 수 없습니다.')
              break
            case 409:
              showError('이미 존재하는 하위분류명입니다.')
              break
            case 500:
              showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
              break
            default:
              showError('하위분류 수정에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          showError('하위분류 수정에 실패했습니다. 다시 시도해주세요.')
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) return
    onClose()
  }

  const isFormValid = categoryName.trim()

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  if (!performanceCategory) {
    return null
  }

  const typeLabel = getPerformanceTypeLabel(
    performanceCategory.type as 'business' | 'performance_category' | 'performance',
    performanceCategory.linkType
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="하위분류 수정"
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-6" 
        style={{ 
          padding: '24px',
        }}
      >

        {/* Section 1: 기본정보 */}
        <div className="space-y-4">
          <SectionHeader title="기본정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color="info" size="small">
                  {typeLabel}
                </Tag>
              }
            />
          </div>
        </div>

        {/* Section 2: 분류 정보 */}
        <div className="space-y-4">
          <SectionHeader title="하위분류 정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label={<RequiredLabel>하위분류명</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="하위분류명을 입력하세요"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            {/* 수동 가중치 설정 */}
            <InfoField
              label="가중치 설정"
              fullWidth={true}
              value={
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isManualWeight}
                      onChange={(e) => {
                        setIsManualWeight(e.target.checked)
                        if (!e.target.checked) {
                          setManualWeight(null)
                        }
                      }}
                      disabled={isSubmitting}
                      className="rounded"
                    />
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[6]
                    }}>
                      수동 가중치 사용
                    </span>
                  </label>

                  {isManualWeight && (
                    <input
                      type="number"
                      value={manualWeight || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value)
                        setManualWeight(value)
                      }}
                      placeholder="가중치를 입력하세요 (0-100)"
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full p-2 border rounded"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        color: designTokens.colors.gray[6]
                      }}
                      disabled={isSubmitting}
                    />
                  )}

                  <p style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '12px',
                    color: designTokens.colors.gray[4]
                  }}>
                    {isManualWeight
                      ? '수동으로 설정된 가중치가 적용됩니다.'
                      : '자동으로 계산된 가중치가 적용됩니다.'
                    }
                  </p>
                </div>
              }
            />

          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default PerformanceCategoryEditPanel